import React, { useEffect, useState, useCallback } from "react";
import classnames from "classnames";
import styles from "./BackToTop.module.scss";
import AccessibilityLabel from "@unum/accessibility-label";
import MicroCopy from "@unum/micro-copy";
import ArrowTopIcon from "./assets/arrow-top-icon.inline.svg";

type Props = {
  appearOn?: number;
  onScrollToTop?: () => void;
};

const BackToTop = ({ appearOn = 0, onScrollToTop }: Props) => {
  const [isVisible, setIsVisible] = useState(appearOn <= 0 ? true : false);

  useEffect(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    if (appearOn <= 0) {
      return;
    }

    const onScroll = () => {
      if (window.pageYOffset > appearOn) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  });

  const scrollToTopHandler = useCallback(() => {
    if (!isVisible) {
      return;
    }

    window.scrollTo({ top: 0 });

    if (onScrollToTop) {
      onScrollToTop();
    }
  }, [onScrollToTop, isVisible]);

  return (
    <button
      role="button"
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
      onClick={scrollToTopHandler}
      className={classnames(styles["BackToTop"], {
        [styles["BackToTop--visible"]]: isVisible
      })}
    >
      <ArrowTopIcon />
      <AccessibilityLabel>
        <MicroCopy path="back-to-top.label" />
      </AccessibilityLabel>
    </button>
  );
};

export default BackToTop;
